import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import TextInputWithEmoji from 'components/ReusableComponents/ManageHotel/TextInputWithEmoji'
import Toggle from 'components/ReusableComponents/Toggle'
import { useEffect } from 'react'
import DatePicker from 'react-datepicker'
import { useSelector } from 'react-redux'
import {
  clampDiscountPercentage,
  getPromoCodeWord,
  MAX_DISCOUNT_PERCENTAGE,
  nightsToStayOptions
} from 'utils/promotionHelpers'

import styles from './PromoteLaunchStep.module.scss'

function PromotionFields({
  promotionCode,
  setPromotionCode,
  minNightsToStay,
  setMinNightsToStay,
  maxDiscountPercentage,
  setMaxDiscountPercentage,
  promoTagline,
  setPromoTagline,
  hasPromoValidityDate,
  setHasPromoValidityDate,
  promoValidityStartDate,
  setPromoValidityStartDate,
  promoValidityEndDate,
  setPromoValidityEndDate,
  isUsingPromotion,
  setIsUsingPromotion,
  isPromoValid
}) {
  const parentChainCode = useSelector(s => s.hotelDetails.parentChainCode)

  const promoCodeWord = getPromoCodeWord(parentChainCode)

  useEffect(() => {
    if (!isUsingPromotion) {
      setPromotionCode(null)
      setMinNightsToStay(1)
      setMaxDiscountPercentage(0)
    }
  }, [isUsingPromotion]) // eslint-disable-line

  function handleDiscountChange(event) {
    setMaxDiscountPercentage(clampDiscountPercentage(event.target.value))
  }

  function handleValidityRangeChange(dates) {
    const [start, end] = dates
    setPromoValidityStartDate(start)
    setPromoValidityEndDate(end)
  }

  return (
    <>
      <div className={styles.toggleRow}>
        <div className={styles.toggleCopy}>
          <p className={styles.toggleTitle}>Use promotion</p>
          <p className={styles.toggleDescription}>
            Turn off to send the campaign without a discount.
          </p>
        </div>
        <Toggle
          checked={isUsingPromotion}
          handleChange={event => setIsUsingPromotion(event.target.checked)}
        />
      </div>

      <div className={isUsingPromotion ? styles.fieldGroup : styles.fieldGroupDisabled}>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Offer {promoCodeWord}</span>
            <TextInput
              className={styles.fullWidthInput}
              value={promotionCode || ''}
              valid={isPromoValid}
              onChange={event => setPromotionCode(event.target.value)}
            />
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Minimum nights</span>
            <Dropdown
              className={styles.fullWidthInput}
              disabled={!promotionCode}
              options={nightsToStayOptions}
              value={minNightsToStay}
              onChange={event => setMinNightsToStay(event.target.value)}
            />
          </div>
        </div>
        {promotionCode && !isPromoValid && (
          <p className={styles.fieldError}>Enter a valid promo code</p>
        )}

        <div className={styles.field}>
          <div className={styles.fieldLabelRow}>
            <span className={styles.fieldLabel}>Discount percentage</span>
            <span className={styles.fieldHint}>% off direct bookings</span>
          </div>
          <TextInput
            className={styles.fullWidthInput}
            type="number"
            min={0}
            max={MAX_DISCOUNT_PERCENTAGE}
            value={maxDiscountPercentage ?? 0}
            onChange={handleDiscountChange}
            onBlur={event =>
              event.target.value === '' ? setMaxDiscountPercentage(0) : false
            }
          />
        </div>

        <div className={styles.field}>
          <div className={styles.fieldLabelRow}>
            <span className={styles.fieldLabel}>Offer tagline</span>
            <span className={styles.fieldHint}>Shown on the promo card</span>
          </div>
          <TextInputWithEmoji
            className={styles.fullWidthInput}
            value={promoTagline}
            onValueChange={setPromoTagline}
            maxLength={20}
          />
        </div>

        <div className={styles.toggleRow}>
          <div className={styles.toggleCopy}>
            <p className={styles.toggleTitle}>Offer is valid between specific dates</p>
          </div>
          <Toggle
            checked={hasPromoValidityDate}
            handleChange={event => setHasPromoValidityDate(event.target.checked)}
          />
        </div>
        {hasPromoValidityDate && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Valid date range</span>
            <DatePicker
              onChange={handleValidityRangeChange}
              startDate={promoValidityStartDate}
              endDate={promoValidityEndDate}
              selectsRange
              customInput={<TextInput className={styles.fullWidthInput} />}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default PromotionFields
